import { Card } from '@vitness/ui';
import { Money } from '../../components/Money';
import { Num } from '../../components/ui';
import { useCountUp } from '../../lib/useCountUp';
import { fmtMoney } from '../../lib/format';

/**
 * The answer to "am I okay?", before anything else on the page.
 *
 * One large figure — unallocated dollars per month — with a one-sentence
 * verdict under it. Everything below the hero is supporting detail; this is
 * the only block a phone shows above the fold, so it has to carry the whole
 * story: direction (colour + sign), magnitude (the number), and context
 * (runway, freshness).
 */
export function Hero({
  surplus, bufferMonths, syncAgeMinutes,
}: {
  surplus: number;
  bufferMonths: number;
  syncAgeMinutes: number | null;
}) {
  const animated = useCountUp(surplus);
  const ok = surplus >= 0;

  const verdict = ok
    // Spelled out rather than "of fixed costs": the runway is priced at fixed
    // costs plus half of guilt-free, so calling it fixed costs understated what
    // the buffer is actually being measured against.
    ? `You're ${fmtMoney(surplus)}/mo in the black; your buffer covers ${bufferMonths.toFixed(1)} months of fixed costs + 50% of guilt-free spending.`
    : `You're ${fmtMoney(-surplus)}/mo over budget; your buffer covers ${bufferMonths.toFixed(1)} months of fixed costs + 50% of guilt-free spending.`;

  return (
    <Card variant="outlined" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <Money value={Math.round(animated)} size="hero" suffix="/mo"
          style={{ color: ok ? 'var(--vn-success)' : 'var(--vn-danger)' }} tone="plain" />
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
          color: ok ? 'var(--vn-success)' : 'var(--vn-danger)',
          background: ok ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
        }}>
          {ok ? 'unallocated' : 'over budget'}
        </span>
      </div>

      <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--vn-text-secondary)', maxWidth: 560 }}>
        {verdict}
      </p>

      {syncAgeMinutes !== null && (
        <Num style={{ display: 'block', marginTop: 8, fontSize: 10.5, color: 'var(--vn-text-muted)' }}>
          {freshness(syncAgeMinutes)}
        </Num>
      )}
    </Card>
  );
}

function freshness(mins: number): string {
  if (mins < 2) return 'Synced just now';
  if (mins < 90) return `Synced ${Math.round(mins)} min ago`;
  if (mins < 36 * 60) return `Synced ${Math.round(mins / 60)}h ago`;
  return `Synced ${Math.round(mins / (24 * 60))} days ago — pull down to refresh`;
}
